import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { prospectsAPI, wallAPI } from '@/lib/api';
import { setBackGuard, clearBackGuard } from '@/lib/backGuard';
import { loadProspectDetail, prospectDetailKey } from '@/lib/resources';
import { getCached, invalidate, invalidatePrefix } from '@/lib/dataCache';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { 
    Phone, Mail, MapPin, Edit, Trash2, 
    Handshake, Wrench, Users, Crown, Building2, XCircle, AlertTriangle,
    TrendingUp, Wallet, User, FileText
} from 'lucide-react';
import { ClickableUser } from '@/components/shared/ClickableUser';
import { Copyable } from '@/components/common/Copyable';
import { OverviewButton, canGoBack } from '@/components/common/NavButtons';import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import ProspectNavigationBar from '@/components/prospect/ProspectNavigationBar';
import { CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { StageChanger } from '@/components/prospect/StageChanger';
import { ProspectAttachments } from '@/components/prospect/ProspectAttachments';
import { EasyPowerModule } from '@/components/prospect/EasyPowerModule';
import { CoopModeButton } from '@/components/prospect/CoopModeButton';
import { CommissionUnifiee } from '@/components/prospect/CommissionUnifiee';
import { ProspectComponents } from '@/components/prospect/ProspectComponents';
import { Prolongements } from '@/components/prospect/Prolongements';
import { DeleteProspectDialog } from '@/components/prospect/DeleteProspectDialog';
import ChantiersTabs from '@/components/prospect/ChantiersTabs';
import ChantierDocsSection from '@/components/prospect/ChantierDocsSection';
import ChantierAuditorSection from '@/components/prospect/ChantierAuditorSection';
import { ChantierSection } from '@/components/prospect/ChantierSection';
import { QuickTaskEventCreator } from '@/components/prospect/QuickTaskEventCreator';
import { GeoportailModule } from '@/components/prospect/GeoportailModule';
import ChantierQualificationSynthesisPanel from '@/components/prospect/ChantierQualificationSynthesisPanel';
import ChantierQualificationBlock from '@/components/prospect/ChantierQualificationBlock';
import ProspectWall from '@/components/prospect/ProspectWall';
import StudyFormsSection from '@/components/study/StudyFormsSection';
import { StageProgressBar } from '@/components/prospect/StageProgressBar';
import ChantierSubSteps from '@/components/prospect/ChantierSubSteps';
import { ReassignApporteur } from '@/components/prospect/ReassignApporteur';
import { RequestDeletionButton } from '@/components/prospect/RequestDeletionButton';
import { CoproprietesSection } from '@/components/prospect/CoproprietesSection';
import EnergyCardSection from '@/components/prospect/EnergyCardSection';
import ProspectModuleTiles from '@/components/prospect/ProspectModuleTiles';
import '@/components/prospect/prospectModuleTabs.css';
import ProspectSectionNav from '@/components/prospect/ProspectSectionNav';
import { CoopAssignment } from '@/components/prospect/CoopPresentation';
import ProspectStarterAssistant from '@/components/prospect/ProspectStarterAssistant';
import { FileText as SectionIcon, MapPin as SectionMap, Layers as SectionModules, MessageSquare as SectionMessages, Zap as SectionEnergy } from 'lucide-react';

const CLASS_LABELS = {
    agri: 'Agriculture',
    tertiaire: 'Tertiaire',
    renovation: 'Rénovation globale'
};

const fmtDateShort = (iso) => {
    if (!iso) return '';
    try {
        const s = iso.length === 10 ? iso + 'T00:00:00' : iso;
        return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
};

// Choisit le module à ouvrir par défaut sur une fiche dossier :
// - un seul module actif → celui-ci ;
// - deux modules → celui avec la commission la plus importante ;
// - on évite un module PERDU (perdu) si l'autre module est ouvert.
function pickDefaultModule(p) {
    if (!p) return 'easy_power';
    const cand = [];
    if (p.easy_power_stage && !p.easy_power_disabled && p.module_permissions?.easy_power?.read !== false) {
        cand.push({ m: 'easy_power', lost: p.easy_power_stage === 'ep_perdu', com: p.commission_totale_ep || 0 });
    }
    if (p.chantier_stage && !p.chantier_disabled && p.module_permissions?.chantier?.read !== false) {
        cand.push({ m: 'chantier', lost: p.chantier_stage === 'ch_perdu', com: p.commission_totale_ch || 0 });
    }
    if (cand.length === 0) return 'easy_power';
    const nonLost = cand.filter((c) => !c.lost);
    const pool = nonLost.length ? nonLost : cand;
    pool.sort((a, b) => b.com - a.com);
    return pool[0].m;
}


// Bandeau prochaine action AU-DESSUS DU MUR, par module (indépendant comme le mur).
function NextActionBanner({ info, moduleLabel }) {
    if (!info || !info.needs) return null;
    if (info.has_next_action) {
        return (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid={`wall-next-action-ok-${moduleLabel}`}>
                <CalendarClock className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-sm text-emerald-700 font-medium">
                    Prochaine action {moduleLabel} : {info.next_action?.type === 'event' ? 'RDV' : 'Tâche'} le {fmtDateShort(info.next_action?.date)}
                    {info.next_action?.title ? ` — ${info.next_action.title}` : ''}
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-start gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-300" data-testid={`wall-next-action-none-${moduleLabel}`}>
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span className="text-sm text-amber-700 font-medium">
                Aucune prochaine action planifiée sur {moduleLabel} — pensez à programmer une relance, un rappel ou un RDV.
            </span>
        </div>
    );
}

export default function ProspectDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [prospect, setProspect] = useState(null);
    const [loading, setLoading] = useState(true);
    // Onglet actif (contrôlé) — ouvre le bon module si ?module= (clic Pilotage) ou ?wall= (notification)
    const forcedModule = searchParams.get('module') || searchParams.get('wall');
    const [activeTab, setActiveTab] = useState(
        forcedModule === 'chantier' || forcedModule === 'easy_power' ? forcedModule : 'easy_power'
    );
    // N'appliquer le choix automatique de module qu'UNE fois (ne pas écraser un changement d'onglet manuel)
    const autoModuleRef = useRef(forcedModule === 'chantier' || forcedModule === 'easy_power');
    // Bascule sur le bon module si ?module/?wall change (clic notification/Pilotage alors qu'on est déjà sur le dossier)
    useEffect(() => {
        const w = searchParams.get('module') || searchParams.get('wall');
        if (w === 'easy_power' || w === 'chantier') { setActiveTab(w); autoModuleRef.current = true; }
    }, [searchParams]);
    // Choix automatique du module à l'ouverture (sans param explicite) :
    // module actif avec la commission la plus importante, en évitant un module PERDU si l'autre est ouvert.
    useEffect(() => {
        if (!prospect || autoModuleRef.current) return;
        autoModuleRef.current = true;
        // L'AUDITEUR est auditeur UNIQUEMENT sur le module Travaux d’optimisation — jamais Courtage.
        // On force l'ouverture directement sur l'onglet Travaux d’optimisation et on masque l'onglet EP.
        if (prospect?.is_auditor_view === true) {
            setActiveTab('chantier');
            return;
        }
        setActiveTab(pickDefaultModule(prospect));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prospect]);
    // Pastilles "non lus" par module pour ce dossier
    const [tabUnread, setTabUnread] = useState({ easy_power: 0, chantier: 0 });
    
    // Attachments
    const [attachments, setAttachments] = useState([]);
    // Erreur transitoire (timeout/réseau/5xx) → on N'affiche PAS "introuvable", on propose de réessayer
    const [loadError, setLoadError] = useState(false);
    // Rappel "sécuriser le dossier avec une tâche" à la sortie de la fiche
    const [leaveHintOpen, setLeaveHintOpen] = useState(false);
    // Prochaine action planifiée du dossier (tâche/événement à venir)
    const [nextActionInfo, setNextActionInfo] = useState(null);
    const doLeave = () => {
        setLeaveHintOpen(false);
        // Retour intelligent : restaure la page d'origine + focus sur le dossier COURANT
        // (celui qui vient d'être traité, y compris après plusieurs Prev/Next).
        const state = (() => {
            try { const s = sessionStorage.getItem('valnexo_return_state'); return s ? JSON.parse(s) : null; } catch { return null; }
        })();
        if (state && state.path && Date.now() - (state.at || 0) < 30 * 60 * 1000) {
            try {
                if (prospect?.id) sessionStorage.setItem('valnexo_pending_return_focus_id', prospect.id);
                sessionStorage.removeItem('valnexo_return_state');
            } catch { /* noop */ }
            navigate(state.path);
            return;
        }
        if (canGoBack()) navigate(-1); else navigate('/prospects');
    };
    const handleBack = () => {
        // Conservé pour la modale « quitter sans sécuriser » : appelé après confirmation user.
        doLeave();
    };

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    const isParrain = user?.role === 'parrain' && !isAdminFull && !isAdmin;
    const parrainReadOnly = isParrain;
    const chantierAccess = prospect?.module_permissions?.chantier?.read !== false;
    const chantierAuditor = prospect?.is_chantier_auditor_view === true || prospect?.is_auditor_view === true;
    const chantierReadOnly = chantierAuditor || parrainReadOnly || prospect?.module_permissions?.chantier?.write === false;

    useEffect(() => {
        if (!prospect) return;
        if (prospect.module_permissions?.[activeTab]?.read === false) {
            setActiveTab(prospect.module_permissions?.easy_power?.read ? 'easy_power' : 'chantier');
        }
    }, [prospect, activeTab]);
    
    // Tous les utilisateurs peuvent supprimer (avec vérifications côté backend)
    const canDelete = true;

    useEffect(() => {
        loadProspect();
    }, [id]);

    // Guard sur le bouton "Retour" global : si le dossier n'est pas sécurisé (aucune
    // action future planifiée), on ouvre la modale au lieu de laisser le retour partir.
    useEffect(() => {
        setBackGuard(() => {
            if (nextActionInfo && nextActionInfo.unsecured) {
                setLeaveHintOpen(true);
                return false; // annule le navigate(-1)
            }
            return true; // laisse partir
        });
        return () => clearBackGuard();
    }, [nextActionInfo]);

    // Prochaine action planifiée du dossier (pour le badge + la modale de sortie)
    const loadNextAction = () => {
        prospectsAPI.getNextAction(id)
            .then((res) => setNextActionInfo(res.data))
            .catch(() => setNextActionInfo(null));
    };
    useEffect(() => {
        if (id) loadNextAction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Pastilles "nouveaux messages" par module pour ce dossier (1 requête légère)
    useEffect(() => {
        wallAPI.unreadBatch([id]).then((res) => {
            const m = res.data?.[id] || {};
            setTabUnread({ easy_power: m.easy_power || 0, chantier: m.chantier || 0 });
        }).catch(() => {});
    }, [id]);

    const clearTabUnread = (module) => setTabUnread((prev) => ({ ...prev, [module]: 0 }));

    const loadProspect = async () => {
        const key = prospectDetailKey(id);
        const cached = getCached(key);
        if (cached) {
            setProspect(cached.prospect);
            setAttachments(cached.attachments);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setLoadError(false);
        try {
            const data = await loadProspectDetail(id);
            setProspect(data.prospect);
            setAttachments(data.attachments);
        } catch (error) {
            const status = error.response?.status;
            // 404 (vraiment introuvable) ou 403 (pas d'accès) → on redirige.
            // Toute autre erreur (timeout, réseau, 502/503/504) = transitoire sous charge :
            // on NE redirige PAS et on NE dit PAS "introuvable" → on propose de réessayer.
            if (status === 404 || status === 403) {
                invalidate(prospectDetailKey(id));
                setProspect(null);
                setAttachments([]);
                toast.error(status === 403 ? 'Accès non autorisé à ce dossier' : 'Dossier introuvable');
                navigate('/prospects');
            } else if (!cached) {
                console.error('Error loading prospect (transient):', error);
                setLoadError(true);
            }
        } finally {
            setLoading(false);
        }
    };

    // Après une mutation : invalider les caches concernés puis recharger en forçant.
    const refreshProspect = async () => {
        invalidate(prospectDetailKey(id));
        invalidatePrefix('prospects');
        invalidatePrefix('dashboard');
        invalidatePrefix('pilotage');
        invalidatePrefix('userprofile');
        try {
            const data = await loadProspectDetail(id, { force: true });
            setProspect(data.prospect);
            setAttachments(data.attachments);
        } catch (error) {
            if ([403, 404].includes(error.response?.status)) {
                invalidate(prospectDetailKey(id));
                setProspect(null);
                setAttachments([]);
                navigate('/prospects');
            }
        }
        loadNextAction();
    };

    const handleDelete = async () => {
        setDeleteDialogOpen(true);
    };

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteProcessing, setDeleteProcessing] = useState(false);
    const prospectDisplayName =
        prospect?.company_name ||
        `${prospect?.first_name || ''} ${prospect?.last_name || ''}`.trim() ||
        prospect?.raison_sociale || '';

    const confirmDelete = async (confirmName) => {
        try {
            setDeleteProcessing(true);
            await prospectsAPI.delete(id, confirmName);
            toast.success('Dossier mis à la corbeille');
            setDeleteDialogOpen(false);
            navigate('/prospects');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la suppression';
            toast.error(message);
        } finally {
            setDeleteProcessing(false);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return `${value.toLocaleString('fr-FR')} €`;
    };


    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    if (loadError && !prospect) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="prospect-load-error">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-orange-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">Chargement impossible</h2>
                        <p className="text-slate-600 mb-4 max-w-md">
                            Le serveur met plus de temps que prévu à répondre (affluence). Le dossier n'est pas perdu — réessayez dans un instant.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <Button variant="outline" onClick={() => navigate('/prospects')} data-testid="prospect-error-back">
                                Retour aux dossiers
                            </Button>
                            <Button
                                onClick={() => { setLoading(true); loadProspect(); }}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="prospect-error-retry"
                            >
                                Réessayer
                            </Button>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (!prospect) return null;

    return (
        <DashboardLayout>
            {/* Barre sticky "Dossier suivant" — affichée seulement si contexte liste */}
            <ProspectNavigationBar
                currentId={prospect.id}
                prospectName={prospect.company_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Dossier'}
                pipelineHint={forcedModule}
            />
            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-5" data-testid="prospect-detail-page">
                {/* Header */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <OverviewButton testid="prospect-overview-btn" />
                        <div className="flex-1" />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="text-base sm:text-xl font-semibold text-slate-600">
                                {prospect.first_name?.[0]}{prospect.last_name?.[0]}
                            </span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg sm:text-2xl font-outfit font-semibold text-slate-900 truncate">
                                {prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}
                            </h1>
                            <p className="text-slate-500 text-xs sm:text-base truncate">
                                {CLASS_LABELS[prospect.prospect_class] || 'Non classé'}
                                {prospect.prospect_subclass && ` - ${prospect.prospect_subclass}`}
                            </p>
                            {/* Badge Mode Coop actif (visible Admin/VP/Parrain, PAS pour l'apporteur Coop lui-même) */}
                            {prospect.coop_mode_enabled && (isAdminFull || isAdmin || user?.role === 'vp' || user?.role === 'parrain') && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5 w-fit" data-testid="coop-mode-badge">
                                    <span>🤝</span>
                                    <span className="font-semibold">Mode Coop actif</span>
                                    {prospect.can_manage_coop && <span className="opacity-70" data-testid="coop-mode-manager-rate">
                                        · {prospect.coop_commission_value}{prospect.coop_commission_mode === 'percent' ? '%' : ' €'}
                                    </span>}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Un parrain garde l'accès QuickTaskEventCreator (tâches + événements
                            pour lui ou pour son VP) car ce sont ses SEULES actions autorisées. */}
                        <QuickTaskEventCreator prospect={prospect} onCreated={refreshProspect} />
                        {/* Un parrain ne peut ni MODIFIER ni SUPPRIMER un dossier — il est
                            spectateur (lecture seule) sur toutes les données du dossier.
                            Seules les tâches / événements + suivi (créés ci-dessus) sont autorisés. */}
                        {!isParrain && (
                            <>
                                {Object.values(prospect.module_permissions || {}).some(rights => rights.write) && <Button variant="outline" size="sm" onClick={() => navigate(`/prospects/${id}/edit`)} data-testid="edit-prospect-btn">
                                    <Edit className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Modifier</span>
                                </Button>}
                                {/* Mode Coop — Admin/VP uniquement, dossiers Courtage parrain sans apporteur d'origine */}
                                {prospect.can_manage_coop && (
                                    <CoopModeButton prospect={prospect} onUpdate={refreshProspect} />
                                )}
                                {isAdminFull && (
                                    <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-prospect-btn" title="Supprimer (réservé Admin Full)">
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        <span className="hidden sm:inline">Supprimer</span>
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Propriété du dossier - Apporteur | VP | Équipe */}
                {(prospect.dossier_apporteur_name || prospect.dossier_vp_name || prospect.team_name || (prospect.coop_mode_enabled && prospect.can_manage_coop)) && (
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="prospect-ownership">
                        {/* Sur un dossier `origin=parrainage`, le porteur est un parrain (pas un
                            apporteur). Le label reflète strictement le rôle métier pour éviter
                            tout quiproquo. */}
                        {prospect.dossier_apporteur_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Users className={`w-4 h-4 ${prospect.origin === 'parrainage' ? 'text-indigo-600' : 'text-green-600'}`} />
                                <span className="text-slate-500">{prospect.origin === 'parrainage' ? 'Parrain:' : 'Apporteur:'}</span>
                                <span className={`font-medium ${prospect.origin === 'parrainage' ? 'text-indigo-700' : 'text-green-700'}`}>
                                    <ClickableUser userId={prospect.dossier_apporteur_id} name={prospect.dossier_apporteur_name} />
                                </span>
                            </div>
                        )}
                        <CoopAssignment prospect={prospect} />
                        {/* Séparateur */}
                        {prospect.dossier_apporteur_name && (prospect.dossier_vp_name || prospect.vp_display_name) && (
                            <span className="text-slate-300">|</span>
                        )}
                        {/* VP - selon rôle */}
                        {(prospect.dossier_vp_name || prospect.vp_display_name) && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Crown className="w-4 h-4 text-purple-600" />
                                <span className="text-slate-500">VP:</span>
                                <span className="font-medium text-purple-700">
                                    <ClickableUser userId={prospect.dossier_vp_id} name={prospect.vp_display_name || prospect.dossier_vp_name} />
                                </span>
                            </div>
                        )}
                        {/* Séparateur */}
                        {(prospect.dossier_apporteur_name || prospect.dossier_vp_name) && (prospect.team_name || prospect.is_vp_direct) && (
                            <span className="text-slate-300">|</span>
                        )}
                        {/* Équipe */}
                        {prospect.team_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                <span className="text-slate-500">Équipe:</span>
                                <span className="font-medium text-blue-700">{prospect.team_name}</span>
                            </div>
                        )}
                        {/* Badge VP Direct */}
                        {prospect.is_vp_direct && !prospect.team_name && (
                            <div className="flex items-center gap-1.5 text-sm">
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md font-medium text-xs">
                                    Dossier VP Direct
                                </span>
                            </div>
                        )}
                        
                        {/* Bouton Réattribuer pour VP */}
                        {(isAdminFull || isAdmin || (isVP && (prospect.dossier_vp_id === user?.id || prospect.dossier_vp_2_id === user?.id))) && (
                            <>
                                <span className="text-slate-300">|</span>
                                <ReassignApporteur prospect={prospect} onReassigned={refreshProspect} />
                            </>
                        )}
                    </div>
                )}

                {/* Historique de rattachement — visible si le dossier a bougé
                    d'équipe (déplacement d'apporteur ou promotion apporteur→VP).
                    Affiché uniquement pour les rôles habilités (admin, VP payeur/superviseur du dossier). */}
                {Array.isArray(prospect.team_history) && prospect.team_history.length > 0 && (isAdmin || (isVP && (prospect.dossier_vp_id === user?.id || prospect.dossier_vp_2_id === user?.id))) && (
                    <details className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="team-history-details">
                        <summary className="cursor-pointer text-slate-600 font-medium hover:text-slate-900">
                            📜 Historique de rattachement ({prospect.team_history.length})
                        </summary>
                        <ul className="mt-2 space-y-1 text-slate-700">
                            {[...prospect.team_history].reverse().map((h, idx) => (
                                <li key={idx} className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-slate-400 tabular-nums">
                                        {h.at ? new Date(h.at).toLocaleDateString('fr-FR') : '—'}
                                    </span>
                                    <span className="text-slate-500">→</span>
                                    <span className="font-medium text-slate-700">{h.new_team_name || 'Sans équipe'}</span>
                                    {h.new_vp_name && (
                                        <>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-purple-700">VP {h.new_vp_name}</span>
                                        </>
                                    )}
                                    {h.reason === 'apporteur_promoted_to_vp' && (
                                        <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px]">Promu VP</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}

                {/* INDICATEUR PROCHAINE ACTION (global dossier) — suivi */}
                {nextActionInfo && (
                    nextActionInfo.unsecured ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-300 text-sm" data-testid="next-action-badge-none">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="text-amber-700 font-medium">Aucune prochaine action planifiée — à relancer</span>
                        </div>
                    ) : nextActionInfo.has_next_action ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="next-action-badge">
                            <CalendarClock className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="text-emerald-700 font-medium">
                                Prochaine action : {nextActionInfo.next_action?.type === 'event' ? 'RDV' : 'Tâche'}
                                {' '}le {fmtDateShort(nextActionInfo.next_action?.date)}
                            </span>
                            {nextActionInfo.next_action?.title && (
                                <span className="text-emerald-600/80 truncate max-w-[220px]">— {nextActionInfo.next_action.title}</span>
                            )}
                        </div>
                    ) : null
                )}

                {/* ============================================================
                    NOUVELLE HIÉRARCHIE — Tuiles Modules + Nav sticky
                    Placés en tête pour permettre de comprendre le dossier ET de
                    naviguer entre les sections sans scroller sans fin.
                    ============================================================ */}
                {!(prospect?.is_auditor_view === true) && (
                    <ProspectStarterAssistant
                        prospect={prospect}
                        onScheduleAction={() => {
                            // Ouvre le composant QuickTaskEventCreator (bouton "Nouveau RDV/Tâche")
                            const btn = document.querySelector('[data-testid="quick-task-event-creator-btn"]')
                                || document.querySelector('[data-testid="new-event-btn"]')
                                || document.querySelector('[data-testid^="quick-task"]');
                            if (btn) btn.click();
                        }}
                    />
                )}

                {!(prospect?.is_auditor_view === true) && (
                    <ProspectModuleTiles
                        prospect={prospect}
                        tabUnread={tabUnread}
                        nextActionInfo={nextActionInfo}
                        onOpen={(mod) => {
                            // Change l'onglet du module puis scroll doux vers la zone
                            setActiveTab(mod);
                            setTimeout(() => {
                                const el = document.getElementById('section-modules');
                                if (el) {
                                    const y = el.getBoundingClientRect().top + window.scrollY - 72;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                }
                            }, 50);
                        }}
                        onActivate={() => {
                            // Redirige vers l'éditeur du dossier pour activer un module
                            navigate(`/prospects/${id}/edit`);
                        }}
                    />
                )}

                {/* Nav sticky : sauter d'une section à l'autre sans scroll infini */}
                <ProspectSectionNav
                    items={[
                        { id: 'section-overview', label: "Vue d'ensemble", icon: SectionIcon },
                        { id: 'section-qualif', label: 'Qualification', icon: SectionMap },
                        { id: 'section-modules', label: 'Modules', icon: SectionModules },
                        { id: 'section-discussions', label: 'Discussions', icon: SectionMessages,
                            badge: (tabUnread.easy_power || 0) + (tabUnread.chantier || 0) },
                        ...(chantierAccess ? [{ id: 'section-energy-card', label: 'Carte énergétique', icon: SectionEnergy }] : []),
                    ]}
                />

                {/* Ancre : vue d'ensemble (bandeaux + synthèses éventuelles) */}
                <div id="section-overview" data-testid="section-overview" className="scroll-mt-24">

                {/* BANDEAU RAISON DE PERTE - Courtage */}
                {prospect.easy_power_stage === 'ep_perdu' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="loss-reason-banner-ep">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <Handshake className="w-4 h-4 text-amber-600" />
                                    <span className="font-semibold text-red-700">Courtage - Affaire perdue</span>
                                </div>
                                {prospect.ep_loss_reason && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Raison : {prospect.ep_loss_reason}
                                    </p>
                                )}
                                {prospect.ep_loss_reason_comment && (
                                    <p className="text-sm text-red-600/80 mt-1 italic">
                                        "{prospect.ep_loss_reason_comment}"
                                    </p>
                                )}
                                {prospect.ep_loss_reason_date && (
                                    <p className="text-xs text-red-400 mt-2">
                                        Perdu le {format(new Date(prospect.ep_loss_reason_date), 'dd MMMM yyyy', { locale: fr })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* BANDEAU RAISON DE PERTE - Travaux d’optimisation */}
                {prospect.chantier_stage === 'ch_perdu' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="loss-reason-banner-ch">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <Wrench className="w-4 h-4 text-blue-600" />
                                    <span className="font-semibold text-red-700">Travaux d’optimisation - Affaire perdue</span>
                                </div>
                                {prospect.ch_loss_reason && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Raison : {prospect.ch_loss_reason}
                                    </p>
                                )}
                                {prospect.ch_loss_reason_comment && (
                                    <p className="text-sm text-red-600/80 mt-1 italic">
                                        "{prospect.ch_loss_reason_comment}"
                                    </p>
                                )}
                                {prospect.ch_loss_reason_date && (
                                    <p className="text-xs text-red-400 mt-2">
                                        Perdu le {format(new Date(prospect.ch_loss_reason_date), 'dd MMMM yyyy', { locale: fr })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* BADGE SYNTHÈSE CHANTIER - Visible admins uniquement */}
                {(isAdminFull || isAdmin) && prospect.chantier_stage && !prospect.chantier_disabled && (prospect.commission_totale_ch || 0) > 0 && (
                    <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 p-4 shadow-sm" data-testid="chantier-synthesis-badge">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.08),transparent_60%)] pointer-events-none" />
                        <div className="relative flex items-center gap-2 mb-3">
                            <Wrench className="w-4 h-4 text-blue-700" />
                            <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                                Synthèse Travaux
                            </h3>
                            {prospect.chantiers_count > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                    {prospect.chantiers_count} chantier{prospect.chantiers_count > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <div className="relative grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-blue-100" data-testid="synth-total">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Total HT</div>
                                <div className="text-lg font-bold text-slate-900 mt-0.5">
                                    {(prospect.commission_totale_ch || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-purple-100" data-testid="synth-vp">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Crown className="w-3 h-3 text-purple-600" /> VP nette
                                </div>
                                <div className="text-lg font-bold text-purple-700 mt-0.5">
                                    {(prospect.ch_commission_vp_payeur_nette || prospect.ch_commission_vp || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-emerald-100" data-testid="synth-app">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Users className="w-3 h-3 text-emerald-600" /> Apporteur
                                </div>
                                <div className="text-lg font-bold text-emerald-700 mt-0.5">
                                    {(prospect.ch_commission_apporteur_1 || prospect.ch_commission_apporteur || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-amber-100" data-testid="synth-valnexo">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Building2 className="w-3 h-3 text-amber-600" /> Valnexo
                                </div>
                                <div className="text-lg font-bold text-amber-700 mt-0.5">
                                    {(prospect.ch_restant_valnexo || prospect.ch_commission_valnexo || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-slate-200" data-testid="synth-at">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                    <Wallet className="w-3 h-3 text-slate-600" /> AT Service
                                </div>
                                <div className="text-lg font-bold text-slate-700 mt-0.5">
                                    {(prospect.ch_restant_at_service || prospect.ch_commission_at_service || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {prospect.origin === 'parrainage' && prospect.parrainage_snapshot && !isParrain && (
                    <Card className="border-indigo-200 bg-indigo-50/40" data-testid="dossier-parrainage-block">
                        <CardHeader>
                            <CardTitle className="text-indigo-900 flex items-center gap-2">Réseau Parrainage</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <p className="text-xs text-slate-500">Parrain apporteur</p>
                                    <p className="font-medium text-slate-900">{prospect.parrainage_snapshot.parrain_apporteur_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Niveau</p>
                                    <p className="font-medium text-slate-900">Niveau {prospect.parrainage_snapshot.parrain_apporteur_level}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Branche</p>
                                    <p className="font-medium text-slate-900">{prospect.parrainage_snapshot.branch || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Date de création</p>
                                    <p className="font-medium text-slate-900">{new Date(prospect.parrainage_snapshot.created_at).toLocaleDateString('fr-FR')}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500 mb-2">Chaîne de rémunération (figée à la création)</p>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="px-3 py-1 rounded-full bg-slate-900 text-white text-xs">VP</span>
                                    {(prospect.parrainage_snapshot.recipients || []).slice().reverse().map((r) => (
                                        <span key={r.parrain_id} className="px-3 py-1 rounded-full bg-white border border-indigo-200 text-indigo-800 text-xs">
                                            N{r.level} · {r.parrain_name} ({r.ep_rate}% Courtage / {r.ch_rate}% Travaux)
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Section « Commissions Parrains » retirée de la synthèse (Jérémy 06/02/2026) :
                                l'information est désormais UNIQUEMENT dans le module de commission (bloc
                                Commission Courtage / Commission Travaux plus bas). Plus de doublon, un
                                seul endroit à regarder. La chaîne visuelle VP → N1 → N2 → N3 reste
                                affichée en haut du bloc « Réseau Parrainage » pour donner le contexte
                                structurel du réseau. */}
                        </CardContent>
                    </Card>
                )}

                </div>{/* /section-overview */}

                <div id="section-qualif" data-testid="section-qualif" className="scroll-mt-24 space-y-4 sm:space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Informations société / contrat — fusion Client + Courtage.
                        Visible sur les 2 onglets EP et Travaux d’optimisation. Les données EP-spécifiques
                        (raison_sociale, adresse_contrat, contact, gerant) affichent en fallback
                        les infos de base du prospect si le module EP n'a pas été renseigné. */}
                    {(() => {
                        const ep = prospect.easy_power_data || {};
                        const epContact = ep.contact || {};
                        const epGerant = ep.gerant || {};
                        // FIX : les infos du prospect de BASE priment sur easy_power_data
                        // pour que les modifs faites via le formulaire d'édition du dossier
                        // remontent IMMÉDIATEMENT sur la fiche. easy_power_data ne sert plus
                        // que de fallback si le champ base est vide (rétrocompatibilité).
                        const raisonSociale = prospect.company_name || ep.raison_sociale || '';
                        const siren = (prospect.siret ? String(prospect.siret).slice(0, 9) : '') || ep.siren || '';
                        const adresseContrat = prospect.address || ep.adresse_contrat || '';
                        const cpContrat = prospect.postal_code || ep.code_postal || '';
                        const villeContrat = prospect.city || ep.ville || '';
                        const baseNom = [prospect.first_name, prospect.last_name].filter(Boolean).join(' ');
                        const epNom = [epContact.prenom, epContact.nom].filter(Boolean).join(' ');
                        const contactNom = baseNom || epNom;
                        const contactEmail = prospect.email || epContact.email || '';
                        const contactTel = prospect.phone || epContact.telephone || '';
                        const gerantNom = [epGerant.prenom, epGerant.nom].filter(Boolean).join(' ');
                        return (
                            <Card data-testid="dossier-info-card">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                        Informations société / contrat
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                    {/* Société */}
                                    <div className="p-3 rounded-lg border bg-slate-50/60">
                                        <div className="flex items-center gap-2 mb-2 text-slate-600">
                                            <Building2 className="w-4 h-4" />
                                            <span className="font-medium">Société</span>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="flex items-start gap-1">
                                                <span className="text-slate-500 shrink-0">Raison sociale : </span>
                                                {raisonSociale ? (
                                                    <Copyable value={raisonSociale} label="Raison sociale" testId="copy-raison-sociale">
                                                        <strong data-testid="info-raison-sociale">{raisonSociale}</strong>
                                                    </Copyable>
                                                ) : (
                                                    <span className="text-slate-400" data-testid="info-raison-sociale">Non renseigné</span>
                                                )}
                                            </p>
                                            <p className="flex items-start gap-1">
                                                <span className="text-slate-500 shrink-0">SIREN : </span>
                                                {siren ? (
                                                    <Copyable value={siren} label="SIREN" testId="copy-siren">
                                                        <strong data-testid="info-siren">{siren}</strong>
                                                    </Copyable>
                                                ) : (
                                                    <span className="text-slate-400" data-testid="info-siren">-</span>
                                                )}
                                            </p>
                                            {prospect.siret && (
                                                <p className="flex items-start gap-1">
                                                    <span className="text-slate-500 shrink-0">SIRET (prospect) : </span>
                                                    <Copyable value={prospect.siret} label="SIRET" testId="copy-siret">
                                                        <strong data-testid="info-siret">{prospect.siret}</strong>
                                                    </Copyable>
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Adresse du contrat */}
                                    <div className="p-3 rounded-lg border bg-slate-50/60">
                                        <div className="flex items-center gap-2 mb-2 text-slate-600">
                                            <MapPin className="w-4 h-4" />
                                            <span className="font-medium">Adresse du contrat</span>
                                        </div>
                                        <div className="text-slate-800" data-testid="info-adresse-contrat">
                                            {adresseContrat ? (
                                                <Copyable
                                                    value={[adresseContrat, [cpContrat, villeContrat].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                                                    label="Adresse"
                                                    testId="copy-adresse"
                                                    className="items-start"
                                                >
                                                    <span>
                                                        <span className="block">{adresseContrat}</span>
                                                        <span className="block">{cpContrat} {villeContrat}</span>
                                                    </span>
                                                </Copyable>
                                            ) : (
                                                <span className="text-slate-400">Non renseigné</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contact principal */}
                                    <div className="p-3 rounded-lg border bg-slate-50/60">
                                        <div className="flex items-center gap-2 mb-2 text-slate-600">
                                            <User className="w-4 h-4" />
                                            <span className="font-medium">Contact principal</span>
                                        </div>
                                        <div className="space-y-1">
                                            {contactNom ? (
                                                <p><Copyable value={contactNom} label="Nom" testId="copy-contact-nom">
                                                    <span className="font-medium text-slate-900" data-testid="info-contact-nom">{contactNom}</span>
                                                </Copyable></p>
                                            ) : (
                                                <p><span className="text-slate-400" data-testid="info-contact-nom">Non renseigné</span></p>
                                            )}
                                            {contactTel && (
                                                <p className="flex items-center gap-1.5 text-slate-700">
                                                    <Phone className="w-3.5 h-3.5 shrink-0" />
                                                    <Copyable value={contactTel} label="Téléphone" testId="copy-contact-tel">
                                                        <a href={`tel:${contactTel}`} className="hover:text-blue-600" data-testid="info-contact-tel">{contactTel}</a>
                                                    </Copyable>
                                                </p>
                                            )}
                                            {contactEmail && (
                                                <p className="flex items-center gap-1.5 text-slate-700 break-all">
                                                    <Mail className="w-3.5 h-3.5 shrink-0" />
                                                    <Copyable value={contactEmail} label="Email" testId="copy-contact-email">
                                                        <a href={`mailto:${contactEmail}`} className="hover:text-blue-600" data-testid="info-contact-email">{contactEmail}</a>
                                                    </Copyable>
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Gérant (si différent) */}
                                    <div className="p-3 rounded-lg border bg-slate-50/60">
                                        <div className="flex items-center gap-2 mb-2 text-slate-600">
                                            <Crown className="w-4 h-4" />
                                            <span className="font-medium">Gérant (si différent)</span>
                                        </div>
                                        <div data-testid="info-gerant">
                                            {gerantNom ? (
                                                <Copyable value={gerantNom} label="Nom gérant" testId="copy-gerant">
                                                    <span className="font-medium text-slate-900">{gerantNom}</span>
                                                </Copyable>
                                            ) : (
                                                <p className="text-slate-400 italic">Identique au contact principal</p>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {/* Project Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Détails du Dossier</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-slate-500">Classification</p>
                                <p className="font-medium text-slate-900">
                                    {CLASS_LABELS[prospect.prospect_class] || 'Non classé'}
                                    {prospect.prospect_subclass && ` → ${prospect.prospect_subclass}`}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Créé le</p>
                                <p className="font-medium text-slate-900">
                                    {format(new Date(prospect.created_at), 'dd MMMM yyyy', { locale: fr })}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Dernière mise à jour</p>
                                <p className="font-medium text-slate-900">
                                    {format(new Date(prospect.updated_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* SECTION COPROPRIÉTÉS (uniquement pour un prospect Syndic parent) */}
                {prospect.prospect_class === 'renovation' && prospect.prospect_subclass === 'syndic' && !prospect.parent_prospect_id && (
                    <div className="mb-6">
                        <CoproprietesSection prospectId={prospect.id} enseigne={prospect.enseigne || prospect.company_name} />
                    </div>
                )}

                {/* BLOC QUALIFICATION CHANTIER — placé JUSTE AU-DESSUS de Géoportail
                    (spec §9). Non bloquant, jamais obligatoire, ouvre le formulaire sur
                    valnexo.fr via un token signé 15 min. */}
                <div id="chantier-qualif-block" className="scroll-mt-24">
                <ChantierQualificationBlock
                    prospect={prospect}
                    isParrain={isParrain}
                    isVP={isVP}
                    isAdmin={isAdminFull || isAdmin}
                />
                </div>

                {/* MODULE GÉOPORTAIL — vue satellite du site basée sur l'adresse du dossier.
                    Collapsible (fermé par défaut), géocodage automatique via API BAN, outils
                    de mesure distance/surface, bouton d'ouverture externe vers Géoportail pour
                    la thermographie et la détection de groupes froids. */}
                <GeoportailModule prospect={prospect} />

                {/* COMPOSANTES DU DOSSIER (Courtage / Travaux d’optimisation) — masquées pour l'auditeur
                    (l'auditeur n'a rien à activer/désactiver et n'a pas à voir le module EP) */}
                {!(prospect?.is_auditor_view === true) && (
                    <ProspectComponents prospect={prospect} onUpdate={refreshProspect} />
                )}

                </div>{/* /section-qualif */}

                <div id="section-modules" data-testid="section-modules" className="scroll-mt-24">

                {/* ONGLETS COURTAGE / TRAVAUX — l'auditeur ne voit QUE l'onglet Travaux d’optimisation */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList aria-label="Modules du dossier" data-testid="prospect-module-tabs" className={cn("prospect-module-tabs grid mb-6", prospect?.is_auditor_view === true ? "grid-cols-1" : "grid-cols-2")}>
                        {!(prospect?.is_auditor_view === true) && (
                            <TabsTrigger value="easy_power" data-testid="module-tab-easy_power" className="prospect-module-tab" disabled={!prospect.easy_power_stage || prospect.easy_power_disabled}>
                                <Handshake className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" aria-hidden="true" />
                                <span className="min-w-0 flex flex-col items-start">
                                    <span className="max-w-full truncate">Courtage</span>
                                    {(!prospect.easy_power_stage || prospect.easy_power_disabled) && <span data-testid="module-tab-status-easy_power" className="text-[10px] sm:text-xs font-normal">{prospect.easy_power_disabled ? 'Masqué' : 'Non activé'}</span>}
                                </span>
                                {tabUnread.easy_power > 0 && (
                                    <span data-testid="tab-wall-badge-easy_power" className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none">
                                        {tabUnread.easy_power > 9 ? '9+' : tabUnread.easy_power}
                                    </span>
                                )}
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="chantier" data-testid="module-tab-chantier" className="prospect-module-tab" disabled={!prospect.chantier_stage || prospect.chantier_disabled || !chantierAccess} title={!chantierAccess ? "Aucun accès Travaux sur ce dossier" : undefined}>
                            <Wrench className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" aria-hidden="true" />
                            {/* Label court en mobile (Travaux) pour éviter le débordement */}
                            <span className="min-w-0 flex flex-col items-start">
                                <span className="max-w-full truncate sm:hidden">Travaux</span>
                                <span className="max-w-full truncate hidden sm:inline">Travaux d’optimisation</span>
                                {(!prospect.chantier_stage || prospect.chantier_disabled || !chantierAccess) && <span data-testid="module-tab-status-chantier" className="text-[10px] sm:text-xs font-normal">{!chantierAccess ? 'Accès limité' : prospect.chantier_disabled ? 'Masqué' : 'Non activé'}</span>}
                            </span>
                            {tabUnread.chantier > 0 && (
                                <span data-testid="tab-wall-badge-chantier" className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none">
                                    {tabUnread.chantier > 9 ? '9+' : tabUnread.chantier}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* ==================== ONGLET EASY POWER (masqué complètement pour l'auditeur) ==================== */}
                    {!(prospect?.is_auditor_view === true) && (
                    <TabsContent value="easy_power" className="space-y-6">
                        {/* Barre de progression des étapes — readOnly pour l'auditeur (jamais éditeur) */}
                        <StageProgressBar prospect={prospect} pipelineType="easy_power" onUpdate={refreshProspect} readOnly={prospect?.is_auditor_view === true || parrainReadOnly} />

                        {/* MUR DU DOSSIER — Courtage (entre pipeline et commissions) */}
                        <div id={activeTab === 'easy_power' ? 'section-discussions' : undefined} data-testid="section-discussions-easy_power" className="scroll-mt-24 space-y-6">
                        <NextActionBanner info={nextActionInfo?.easy_power} moduleLabel="Courtage" />
                        <ProspectWall key={`${prospect.id}:easy_power`} prospectId={prospect.id} module="easy_power" moduleLabel="Courtage" onRead={clearTabUnread} canAttach={prospect.module_permissions?.easy_power?.write} />
                        </div>

                        {/* COMMISSION UNIFIÉE EP — masquée pour l'auditeur (jamais de commission)
                            SAUF s'il est aussi apporteur du dossier (dans ce cas `is_auditor_view` est
                            False côté backend et il voit sa com avec les règles apporteur classiques).
                            Un PARRAIN voit ici sa commission personnelle + cascade filtrée (Mes filleuls). */}
                        {!(prospect?.is_auditor_view === true) && (
                            <CommissionUnifiee prospect={prospect} onUpdate={refreshProspect} pipelineType="easy_power" />
                        )}

                        {/* MODULE EASY POWER STRUCTURÉ */}
                        <EasyPowerModule prospect={prospect} onUpdate={refreshProspect} />

                        {/* Stage Changer EP - masqué pour l'auditeur ET pour un parrain (spectateur) */}
                        {!(prospect?.is_auditor_view === true) && !parrainReadOnly && (
                            <StageChanger prospect={prospect} onStageChanged={refreshProspect} pipelineType="easy_power" />
                        )}

                        {/* Prolongements Courtage */}
                        <Prolongements 
                            prospectId={id}
                            prospect={prospect}
                            onRefresh={refreshProspect}
                        />

                        {/* Pièces jointes EP (autres documents) */}
                        <ProspectAttachments 
                            prospectId={id}
                            attachments={(attachments || []).filter(a => a.category !== 'chantier' && a.attachment_type !== 'FACTURE')}
                            onRefresh={refreshProspect}
                            canDelete={!parrainReadOnly && (isAdminFull || isAdmin || isVP)}
                            category="easy_power"
                            title="Autres Pièces Jointes"
                        />
                    </TabsContent>
                    )}
                    
                    {/* ==================== ONGLET TRAVAUX D’OPTIMISATION ==================== */}
                    {chantierAccess && <TabsContent value="chantier" className="space-y-6">
                        {/* Barre de progression des étapes Travaux d’optimisation */}
                        <StageProgressBar prospect={prospect} pipelineType="chantier" onUpdate={refreshProspect} readOnly={chantierReadOnly} />

                        {/* COMMISSION UNIFIÉE CH — visible UNIQUEMENT pour les parrains ici :
                            ils accèdent ainsi à leur commission Travaux d'optimisation + cascade
                            "Mes filleuls" filtrée. Admin/VP conservent leur UI Chantier existante
                            (ChantiersTabs), inchangée par cette refonte. */}
                        {isParrain && prospect?.origin === 'parrainage' && (
                            <CommissionUnifiee prospect={prospect} onUpdate={refreshProspect} pipelineType="chantier" />
                        )}

                        {/* Auditeur du dossier (Travaux uniquement) */}
                        <ChantierAuditorSection prospect={prospect} onUpdate={refreshProspect} />

                        {/* RELEVÉ D'ÉTUDE SUR PLACE (natif, hors-ligne) — sous le pipeline, au-dessus du Mur */}
                        <div id="section-study-forms" className="scroll-mt-24">
                        <StudyFormsSection prospectId={prospect.id} readOnly={parrainReadOnly} />
                        </div>

                        {/* ÉTUDES & DEVIS (documents téléversés, visionnables page par page) — avant le Mur */}
                        <div id="chantier-docs-etude" className="scroll-mt-24">
                        <ChantierDocsSection prospectId={prospect.id} category="etude" readOnly={chantierAuditor ? false : parrainReadOnly} />
                        </div>
                        <div id="chantier-docs-devis" className="scroll-mt-24">
                        <ChantierDocsSection prospectId={prospect.id} category="devis" readOnly={chantierAuditor ? false : parrainReadOnly} />
                        </div>

                        {/* PANNEAU SYNTHÈSE QUALIFICATION — juste avant le mur */}
                        <ChantierQualificationSynthesisPanel prospect={prospect} currentUser={user} />

                        {/* MUR DU DOSSIER — Travaux d’optimisation (entre pipeline et commissions) */}
                        <div id={activeTab === 'chantier' ? 'section-discussions' : undefined} data-testid="section-discussions-chantier" className="scroll-mt-24 space-y-6">
                        <NextActionBanner info={nextActionInfo?.chantier} moduleLabel="Travaux d’optimisation" />
                        <ProspectWall key={`${prospect.id}:chantier`} prospectId={prospect.id} module="chantier" moduleLabel="Travaux d’optimisation" onRead={clearTabUnread} canAttach={!chantierReadOnly} />
                        </div>

                        {/* Sous-étapes structurées (Lot 4) selon l'étape Travaux d’optimisation */}
                        <ChantierSubSteps
                            key={prospect.chantier_stage}
                            stage={prospect.chantier_stage}
                            substeps={prospect.chantier_substeps}
                            readOnly={chantierReadOnly}
                            onSave={async (substeps) => {
                                try {
                                    await prospectsAPI.updateChantierSubsteps(prospect.id, substeps);
                                    refreshProspect();
                                } catch (e) {
                                    toast.error("Erreur lors de l'enregistrement des sous-étapes");
                                    throw e;
                                }
                            }}
                        />

                        {/* MULTI-CHANTIERS INDÉPENDANTS */}
                        <ChantiersTabs prospectId={prospect.id} onUpdate={refreshProspect} isAuditorView={chantierReadOnly} isParrainageOrigin={prospect?.origin === 'parrainage'} />

                        {/* Stage Changer Travaux d’optimisation (global — masqué pour l'auditeur ET pour un parrain) */}
                        {!chantierReadOnly && (
                            <StageChanger prospect={prospect} onStageChanged={refreshProspect} pipelineType="chantier" />
                        )}
                    </TabsContent>}
                </Tabs>
                </div>{/* /section-modules */}

                {/* ==================== CARTE D'IDENTITÉ ÉNERGÉTIQUE ====================
                    Section dédiée au module Travaux d'optimisation, affichée hors de
                    l'onglet Modules pour ne pas alourdir le chargement du dossier. Le
                    composant est monté paresseusement (lazy-mount) : ses données ne
                    sont chargées que lorsque l'utilisateur clique sur « Afficher ». */}
                {chantierAccess && <div id="section-energy-card" data-testid="section-energy-card" className="scroll-mt-24">
                    <EnergyCardSection prospect={prospect} />
                </div>}
            </div>

            {/* Rappel de sortie : sécuriser le dossier avec une prochaine action */}
            <Dialog open={leaveHintOpen} onOpenChange={setLeaveHintOpen}>
                <DialogContent className="max-w-md" data-testid="prospect-leave-hint">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            Dossier sans prochaine action
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-600 space-y-3">
                        <p>
                            Vous quittez <strong>{prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}</strong> alors
                            qu'<strong>aucune prochaine action n'est planifiée</strong>.
                        </p>
                        <p>
                            Pour un suivi efficace, la règle est simple : <strong>ne jamais laisser un dossier sans prochaine action</strong>,
                            sauf s'il est <strong>gagné</strong> ou <strong>perdu</strong> (avec la raison de la perte renseignée).
                        </p>
                        <p>
                            Planifiez dès maintenant la prochaine étape (relance, rappel, RDV…) pour garder le dossier vivant
                            et maximiser vos chances de transformation. 💪
                        </p>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={doLeave} data-testid="leave-hint-quit">
                            Quitter quand même
                        </Button>
                        <Button onClick={() => setLeaveHintOpen(false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="leave-hint-create-task">
                            Planifier une action maintenant
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteProspectDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                prospectName={prospectDisplayName}
                processing={deleteProcessing}
                onConfirm={confirmDelete}
                mode="trash"
            />
        </DashboardLayout>
    );
}

// Composant pour les notes éditables — retiré (remplacé par le Mur du dossier)

